import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	Box,
	List,
	ListItem,
	ListItemText,
	Typography,
	Chip,
	Alert,
	CircularProgress,
	Divider,
	IconButton,
	Tooltip,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Grid,
} from "@mui/material";
import {
	FiberManualRecord as RecordIcon,
	Stop as StopIcon,
	Save as SaveIcon,
	Close as CloseIcon,
	Delete as DeleteIcon,
	Add as AddIcon,
} from "@mui/icons-material";
import { fetchProjects } from "../store/slices/projectSlice";
import { fetchFolders, createFolder } from "../store/slices/folderSlice";
import {
	getStepDescription,
	getStepIcon,
	withStepIds,
} from "../utils/stepUtils";
import { toBackendDateTime } from "../utils/dateUtils";
import { canModify } from "../utils/permissions";

const RecordTestDialog = ({ open, onClose, onSave }) => {
	const dispatch = useDispatch();
	const [testName, setTestName] = useState("");
	const [startUrl, setStartUrl] = useState("");
	const [isRecording, setIsRecording] = useState(false);
	const [recordedSteps, setRecordedSteps] = useState([]);
	const [duration, setDuration] = useState("00:00");
	const [startTime, setStartTime] = useState(null);
	const [error, setError] = useState("");
	const [saving, setSaving] = useState(false);
	const [projectId, setProjectId] = useState("");
	const [folderId, setFolderId] = useState("");
	const [folderDialogOpen, setFolderDialogOpen] = useState(false);
	const [newFolderName, setNewFolderName] = useState("");
	const [creatingFolder, setCreatingFolder] = useState(false);

	// Get authentication state from Redux
	const { isAuthenticated, token } = useSelector((state) => state.auth);
	const { projects } = useSelector((state) => state.projects);
	const { folders } = useSelector((state) => state.folders);

	useEffect(() => {
		if (!open) {
			// Reset when dialog closes
			setTestName("");
			setStartUrl("");
			setIsRecording(false);
			setRecordedSteps([]);
			setDuration("00:00");
			setStartTime(null);
			setError("");
			setSaving(false);
			setProjectId("");
			setFolderId("");
			setNewFolderName("");
		} else {
			// Fetch projects when dialog opens
			dispatch(fetchProjects());
		}
	}, [open, dispatch]);

	useEffect(() => {
		// Fetch folders when project changes
		if (projectId) {
			dispatch(fetchFolders(projectId));
		}
	}, [projectId, dispatch]);

	useEffect(() => {
		// Listen for messages from the extension
		const messageListener = (event) => {
			if (event.origin !== window.location.origin) {
				return;
			}

			const { action, data } = event.data;
			// Messages with a sessionId belong to a test editor replay session
			if (!open || data?.sessionId) {
				return;
			}

			switch (action) {
				case "RECORDING_STARTED":
					setIsRecording(true);
					setRecordedSteps([]);
					setError("");
					break;

				case "RECORDING_STOPPED":
					setIsRecording(false);
					if (data.steps) {
						setRecordedSteps(data.steps);
					}
					if (data.duration) {
						setDuration(data.duration);
					}
					break;

				case "STEP_RECORDED":
					setRecordedSteps((prev) => [...prev, data.step]);
					break;

				case "RECORDING_REJECTED":
					// Not signed in any more, or a recording runs in another tab
					setIsRecording(false);
					setStartTime(null);
					setError(data?.error || "The extension refused to start recording");
					break;

				case "CLEAR_RECORDING":
					setRecordedSteps([]);
					setDuration("00:00");
					setError("");
					break;

				default:
					break;
			}
		};

		window.addEventListener("message", messageListener);

		// Notify extension that dialog is ready
		if (open) {
			window.postMessage(
				{ action: "TESTIFY_DIALOG_READY" },
				window.location.origin,
			);
		}

		return () => {
			window.removeEventListener("message", messageListener);
		};
	}, [open]);

	useEffect(() => {
		// Update duration timer
		let interval;
		if (isRecording && startTime) {
			interval = setInterval(() => {
				const elapsed = Date.now() - startTime;
				const minutes = Math.floor(elapsed / 60000);
				const seconds = Math.floor((elapsed % 60000) / 1000);
				setDuration(
					`${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`,
				);
			}, 1000);
		}
		return () => {
			if (interval) clearInterval(interval);
		};
	}, [isRecording, startTime]);

	const handleStartRecording = () => {
		if (!startUrl.trim()) {
			setError("Please enter a URL to start recording");
			return;
		}

		setIsRecording(true);
		setRecordedSteps([]);
		setStartTime(Date.now());
		setError("");

		// Notify extension to start recording via recorder-bridge
		window.postMessage(
			{ action: "REQUEST_START_RECORDING", data: { url: startUrl } },
			window.location.origin,
		);
	};

	const handleStopRecording = () => {
		setIsRecording(false);
		window.postMessage(
			{ action: "REQUEST_STOP_RECORDING" },
			window.location.origin,
		);
	};

	const handleClearSteps = () => {
		if (window.confirm("Clear all recorded steps?")) {
			setRecordedSteps([]);
			setDuration("00:00");
			setStartTime(null);
			setError("");
		}
	};

	const handleCreateFolder = async () => {
		if (!newFolderName.trim()) {
			setError("Please enter a folder name");
			return;
		}
		if (!projectId) {
			setError("Please select a project first");
			return;
		}

		try {
			setCreatingFolder(true);
			const folderData = {
				name: newFolderName.trim(),
				projectId: projectId,
				parentFolderId: null,
			};

			const result = await dispatch(createFolder(folderData)).unwrap();
			setFolderId(result.id);
			setFolderDialogOpen(false);
			setNewFolderName("");
			setError("");
		} catch (err) {
			setError(err.message || "Failed to create folder");
		} finally {
			setCreatingFolder(false);
		}
	};

	const handleSave = async () => {
		if (!testName.trim()) {
			setError("Please enter a test name");
			return;
		}

		if (recordedSteps.length === 0) {
			setError("No steps recorded");
			return;
		}

		// Check authentication using both Redux state and localStorage
		const localToken = localStorage.getItem("token");
		console.log("[RecordTestDialog] ========== AUTH CHECK ==========");
		console.log(
			"[RecordTestDialog] isAuthenticated (Redux):",
			isAuthenticated,
		);
		console.log(
			"[RecordTestDialog] token (Redux):",
			token ? token.substring(0, 20) + "..." : "null",
		);
		console.log(
			"[RecordTestDialog] localStorageToken:",
			localToken ? localToken.substring(0, 20) + "..." : "null",
		);
		console.log(
			"[RecordTestDialog] Token length:",
			localToken?.length || 0,
		);
		console.log("[RecordTestDialog] ===================================");

		if (!localToken) {
			const errorMsg = "Not authenticated. Please log in to save tests.";
			setError(errorMsg);
			console.error(
				"[RecordTestDialog] BLOCKED - No token found in localStorage!",
			);
			alert(
				"⚠️ You must be logged in to save tests.\n\nPlease log in first, then try recording again.",
			);
			return;
		}

		setSaving(true);
		setError("");

		try {
			// Recording time: from Start to the last recorded action (how the
			// extension measures a session too)
			const lastStepAt = Math.max(
				...recordedSteps
					.map((step) => new Date(step.timestamp).getTime())
					.filter(Number.isFinite),
			);
			const testData = {
				recordedAt: startTime ? toBackendDateTime(startTime) : undefined,
				recordingDuration:
					startTime && Number.isFinite(lastStepAt)
						? Math.max(0, lastStepAt - startTime)
						: undefined,
				name: testName,
				description: `Recorded test with ${recordedSteps.length} steps`,
				startUrl: startUrl || recordedSteps[0]?.url || "",
				steps: withStepIds(recordedSteps).map((step) => ({
					...step,
					// Convert ISO timestamp string to Unix timestamp (milliseconds)
					timestamp: step.timestamp
						? new Date(step.timestamp).getTime()
						: Date.now(),
				})),
				tags: ["recorded", "extension"],
				projectId: projectId || undefined,
				folderId: folderId || undefined,
			};

			console.log("[RecordTestDialog] Saving test with data:", testData);
			const result = await onSave(testData);
			console.log("[RecordTestDialog] Test saved successfully:", result);
			onClose();
		} catch (err) {
			console.error("[RecordTestDialog] Failed to save test:", err);
			console.error("[RecordTestDialog] Error response:", err.response);
			console.error(
				"[RecordTestDialog] Error status:",
				err.response?.status,
			);
			console.error("[RecordTestDialog] Error data:", err.response?.data);

			let errorMessage = "Failed to save test.";
			if (err.response?.status === 401) {
				errorMessage =
					"Authentication failed. Your session may have expired. Please refresh the page and log in again.";
				// Clear invalid token
				localStorage.removeItem("token");
			} else if (err.response?.data?.message) {
				errorMessage = err.response.data.message;
			} else if (err.message) {
				errorMessage = err.message;
			}

			setError(errorMessage);
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
				<DialogTitle>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 2,
							}}
						>
							<Typography variant="h6">
								Record New Test
							</Typography>
							<Chip
								icon={isRecording ? <RecordIcon /> : null}
								label={isRecording ? "Recording..." : "Ready"}
								color={isRecording ? "error" : "default"}
								size="small"
							/>
						</Box>
						<IconButton
							onClick={onClose}
							disabled={isRecording || saving}
						>
							<CloseIcon />
						</IconButton>
					</Box>
				</DialogTitle>

				<DialogContent dividers>
					{error && (
						<Alert
							severity="error"
							sx={{ mb: 2 }}
							onClose={() => setError("")}
						>
							{error}
						</Alert>
					)}

					<Box sx={{ mb: 3 }}>
						<TextField
							fullWidth
							label="Test Name"
							value={testName}
							onChange={(e) => setTestName(e.target.value)}
							placeholder="e.g., Login Flow Test"
							disabled={isRecording || saving}
							required
							sx={{ mb: 2 }}
						/>

						<TextField
							fullWidth
							label="Start URL"
							value={startUrl}
							onChange={(e) => setStartUrl(e.target.value)}
							placeholder="https://example.com"
							disabled={isRecording || saving}
							required
							helperText="The URL where recording will begin"
							sx={{ mb: 2 }}
						/>

						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<FormControl
									fullWidth
									disabled={isRecording || saving}
								>
									<InputLabel>Project</InputLabel>
									<Select
										value={projectId}
										onChange={(e) => {
											setProjectId(e.target.value);
											setFolderId(""); // Reset folder when project changes
										}}
										label="Project"
									>
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{projects.filter(canModify).map((project) => (
											<MenuItem
												key={project.id}
												value={project.id}
											>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1,
													}}
												>
													<Box
														sx={{
															width: 8,
															height: 8,
															borderRadius: "50%",
															bgcolor:
																project.color ||
																"#3b82f6",
														}}
													/>
													{project.name}
												</Box>
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} md={6}>
								<Box sx={{ display: "flex", gap: 1 }}>
									<FormControl
										fullWidth
										disabled={
											!projectId || isRecording || saving
										}
									>
										<InputLabel>Folder</InputLabel>
										<Select
											value={folderId}
											onChange={(e) =>
												setFolderId(e.target.value)
											}
											label="Folder"
										>
											<MenuItem value="">
												<em>None</em>
											</MenuItem>
											{folders
												.filter(
													(f) =>
														f.projectId ===
															projectId &&
														f.kind !== "API",
												)
												.map((folder) => (
													<MenuItem
														key={folder.id}
														value={folder.id}
													>
														{folder.path ||
															folder.name}
													</MenuItem>
												))}
										</Select>
									</FormControl>
									<Button
										variant="outlined"
										disabled={
											!projectId || isRecording || saving
										}
										onClick={() =>
											setFolderDialogOpen(true)
										}
										sx={{ minWidth: "auto", px: 2 }}
									>
										<AddIcon />
									</Button>
								</Box>
							</Grid>
						</Grid>
					</Box>

					<Box
						sx={{
							mb: 2,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
						}}
					>
						<Box sx={{ display: "flex", gap: 2 }}>
							{!isRecording ? (
								<Button
									variant="contained"
									color="error"
									startIcon={<RecordIcon />}
									onClick={handleStartRecording}
									disabled={saving}
								>
									Start Recording
								</Button>
							) : (
								<Button
									variant="contained"
									color="primary"
									startIcon={<StopIcon />}
									onClick={handleStopRecording}
								>
									Stop Recording
								</Button>
							)}
						</Box>

						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 2,
							}}
						>
							<Typography variant="body2" color="text.secondary">
								Duration: {duration}
							</Typography>
							<Tooltip title="Clear all steps">
								<IconButton
									size="small"
									onClick={handleClearSteps}
									disabled={
										isRecording ||
										recordedSteps.length === 0 ||
										saving
									}
								>
									<DeleteIcon />
								</IconButton>
							</Tooltip>
						</Box>
					</Box>

					<Divider sx={{ mb: 2 }} />

					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						Recorded Steps ({recordedSteps.length})
					</Typography>

					{recordedSteps.length === 0 ? (
						<Alert severity="info" sx={{ mt: 2 }}>
							{isRecording
								? "Recording in progress. Perform actions on the target website..."
								: "No steps recorded yet. Click 'Start Recording' to begin."}
						</Alert>
					) : (
						<Box
							sx={{
								border: 1,
								borderColor: "divider",
								borderRadius: 1,
								maxHeight: 300,
								overflow: "auto",
							}}
						>
							<List dense>
								{recordedSteps.map((step, index) => (
									<ListItem
										key={index}
										divider={
											index < recordedSteps.length - 1
										}
										sx={{
											"&:hover": {
												bgcolor: "action.hover",
											},
										}}
									>
										<ListItemText
											primary={
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1,
													}}
												>
													<Typography
														component="span"
														sx={{
															fontSize: "1.2rem",
														}}
													>
														{getStepIcon(step.type)}
													</Typography>
													<Typography
														component="span"
														variant="body2"
													>
														{getStepDescription(
															step,
														)}
													</Typography>
												</Box>
											}
											secondary={
												<Box
													sx={{
														mt: 0.5,
														display: "flex",
														gap: 1,
													}}
												>
													<Chip
														label={step.type}
														size="small"
													/>
													{step.timestamp && (
														<Typography
															component="span"
															variant="caption"
															color="text.secondary"
														>
															{new Date(
																step.timestamp,
															).toLocaleTimeString()}
														</Typography>
													)}
												</Box>
											}
										/>
									</ListItem>
								))}
							</List>
						</Box>
					)}
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose} disabled={isRecording || saving}>
						Cancel
					</Button>
					<Button
						variant="contained"
						startIcon={
							saving ? (
								<CircularProgress size={20} />
							) : (
								<SaveIcon />
							)
						}
						onClick={handleSave}
						disabled={
							isRecording ||
							recordedSteps.length === 0 ||
							saving ||
							!testName.trim()
						}
					>
						{saving ? "Saving..." : "Save Test"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Create Folder Dialog */}
			<Dialog
				open={folderDialogOpen}
				onClose={() => setFolderDialogOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Create New Folder</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						fullWidth
						label="Folder Name"
						value={newFolderName}
						onChange={(e) => setNewFolderName(e.target.value)}
						placeholder="e.g., Integration Tests"
						sx={{ mt: 2 }}
						onKeyPress={(e) => {
							if (e.key === "Enter" && !creatingFolder) {
								handleCreateFolder();
							}
						}}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setFolderDialogOpen(false);
							setNewFolderName("");
						}}
						disabled={creatingFolder}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleCreateFolder}
						disabled={creatingFolder || !newFolderName.trim()}
					>
						{creatingFolder ? "Creating..." : "Create"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default RecordTestDialog;
